import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertCircle, Building2, Car, CheckCircle2, FileText, HardHat, Mail, Martini, Shield, ShieldCheck, Umbrella } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiError } from "@/lib/api";
import { openAuthedPdf } from "@/lib/openDocument";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

const labels = { commercial_general_liability: "Commercial General Liability", automobile_liability: "Automobile Liability", workers_compensation: "Workers' Compensation", umbrella_liability: "Umbrella Liability", excess_liability: "Excess Liability" };
const coverageIcons = { commercial_general_liability: Building2, automobile_liability: Car, workers_compensation: HardHat, umbrella_liability: Umbrella, excess_liability: Shield };
const isLiquor = (c) => /liquor/i.test(c.otherTypeName || "");
const iconFor = (c) => (isLiquor(c) ? Martini : coverageIcons[c.type] || FileText);

export default function InsuredCoverageProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const canWrite = ["owner", "admin", "reviewer"].includes(user?.role);
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [requesting, setRequesting] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [changing, setChanging] = useState(false);
  const [notifying, setNotifying] = useState(false);

  const load = useCallback(() => api.get(`/vendors/${id}/coverage-profile`).then((r) => setData(r.data)), [id]);
  useEffect(() => {
    load().catch(() => toast.error("We couldn’t load this insurance profile."));
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});
  }, [load]);

  async function toggleGroup(groupId, checked) {
    const current = data.insured.groupIds || [];
    const next = checked ? [...current, groupId] : current.filter((g) => g !== groupId);
    setData((prev) => ({ ...prev, insured: { ...prev.insured, groupIds: next } }));
    try { await api.patch(`/vendors/${id}/groups`, { groupIds: next }); }
    catch (error) { setData((prev) => ({ ...prev, insured: { ...prev.insured, groupIds: current } })); toast.error(formatApiError(error)); }
  }

  async function changeTemplate(templateId) {
    if (!templateId || templateId === data.insured.templateId) return;
    setChanging(true);
    try {
      const vendor = (await api.get(`/vendors/${id}`)).data;
      const res = await api.patch(`/vendors/${id}`, { name: vendor.name, contact_name: vendor.contactName || "", email: vendor.email, phone: vendor.phone || "", tags: vendor.tags || [], template_id: templateId });
      const rv = res.data?.reverification;
      if (rv && rv.hasData === false) toast.info("Template updated. This insured has no certificate on file yet to compare against.");
      else if (rv && rv.deficiencies?.length) toast.warning(`Re-verified: ${rv.deficiencies.length} requirement${rv.deficiencies.length === 1 ? "" : "s"} not met.`);
      else toast.success("Template updated. This insured meets the new requirements.");
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setChanging(false);
    }
  }

  async function toggleReminders(enabled) {
    setData((prev) => ({ ...prev, insured: { ...prev.insured, remindersEnabled: enabled } }));
    try {
      await api.patch(`/vendors/${id}/reminders`, { enabled });
      toast.success(enabled ? "Reminders on for this insured." : "Reminders paused for this insured.");
    } catch (error) {
      setData((prev) => ({ ...prev, insured: { ...prev.insured, remindersEnabled: !enabled } }));
      toast.error(formatApiError(error));
    }
  }

  async function notifyInsured() {
    setNotifying(true);
    try {
      await api.post(`/vendors/${id}/notify-deficiencies`);
      toast.success("We emailed the insured a secure link and the list of requirements to meet.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setNotifying(false);
    }
  }

  async function viewCertificate(documentId) {
    if (!documentId) { toast.error("This certificate isn’t available to view yet."); return; }
    setViewing(documentId);
    try { await openAuthedPdf(`/review/documents/${documentId}/file`); }
    catch (error) { toast.error("We couldn’t open this certificate."); }
    finally { setViewing(null); }
  }

  async function requestVerification(documentId) {
    if (!documentId) { toast.error("This certificate isn’t ready for verification yet."); return; }
    setRequesting(documentId);
    try {
      await api.post("/verifications/requests", { documentId });
      toast.success("Verification requested. Our team will begin outreach.");
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setRequesting(null);
    }
  }

  if (!data) return <section data-testid="insured-coverage-loading">Loading insurance profile…</section>;
  const deficiencies = data.insured.deficiencies || [];

  return (
    <section data-testid="insured-coverage-page">
      <Link className="text-sm font-semibold text-blue-700" data-testid="coverage-back-link" to="/vendors">← Insureds</Link>
      <div className="mt-4 flex justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[.18em] text-blue-700">Insurance profile</p>
          <h1 className="mt-2 text-2xl font-semibold" data-testid="coverage-insured-name">{data.insured.name}</h1>
        </div>
        <StatusBadge status={data.insured.status} />
      </div>

      <div className="mt-6 rounded-lg border bg-white p-5" data-testid="requirements-panel">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-[240px]">
            <p className="text-xs font-bold uppercase text-slate-500">Requirement template</p>
            <Select disabled={!canWrite || changing} onValueChange={changeTemplate} value={data.insured.templateId || ""}>
              <SelectTrigger className="mt-2" data-testid="profile-template-select"><SelectValue placeholder="Select a template" /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.isDefault ? " (Default)" : ""}</SelectItem>)}</SelectContent>
            </Select>
            <p className="mt-2 text-xs text-slate-500">Changing this re-checks the certificate on file against the new requirements — no re-upload or AI re-read needed.</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="reminder-toggle-label"><Checkbox checked={data.insured.remindersEnabled !== false} data-testid="insured-reminders-toggle" disabled={!canWrite} onCheckedChange={(v) => toggleReminders(!!v)} />Send expiration & renewal reminders</label>
        </div>
        {deficiencies.length ? (
          <div className="mt-5 rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="profile-deficiencies">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-900"><AlertCircle size={15} />Does not meet {deficiencies.length} requirement{deficiencies.length === 1 ? "" : "s"}</p>
            <ul className="mt-2 space-y-1 text-sm text-amber-900">{deficiencies.map((d, i) => <li data-testid={`profile-deficiency-${i}`} key={i}>• {d}</li>)}</ul>
            {canWrite && <Button className="mt-4 gap-2" data-testid="notify-insured-button" disabled={notifying} onClick={notifyInsured} size="sm"><Mail size={15} />{notifying ? "Sending…" : "Notify insured to submit a new COI"}</Button>}
          </div>
        ) : (
          <div className="mt-5 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-800" data-testid="profile-compliant"><CheckCircle2 size={15} />Meets all current requirements.</div>
        )}
      </div>

      <div className="mt-6 rounded-lg border bg-white p-5" data-testid="groups-panel">
        <p className="text-xs font-bold uppercase text-slate-500">Groups</p>
        <p className="mt-1 text-xs text-slate-500">Organize this insured by trade or project. It can belong to multiple groups.</p>
        {groups.length ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {groups.map((g) => {
              const member = (data.insured.groupIds || []).includes(g.id);
              return (
                <button className={`rounded-full border px-3 py-1.5 text-sm transition ${member ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 text-slate-600 hover:border-blue-300"} disabled:opacity-60`} data-testid={`group-chip-${g.id}`} disabled={!canWrite} key={g.id} onClick={() => toggleGroup(g.id, !member)} type="button">{member ? "✓ " : "+ "}{g.name}</button>
              );
            })}
          </div>
        ) : (
          <p className="mt-3 text-sm text-slate-500" data-testid="groups-empty">No groups yet. Create groups from the <Link className="font-semibold text-blue-700" to="/groups">Groups</Link> page.</p>
        )}
      </div>

      <div className="mt-6 space-y-4">
        {data.coverages.map((c, i) => {
          const CoverageIcon = iconFor(c);
          return (
          <article className="rounded-lg border bg-white p-5" data-testid={`coverage-card-${i}`} key={`${c.policyNumber}-${i}`}>
            <div className="flex justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">{labels[c.type] || c.otherTypeName || "Insurance coverage"}</h2>
                <p className="text-sm text-slate-500">Policy {c.policyNumber || "Not listed"} · {c.effectiveDate || "—"} to {c.expirationDate || "—"}</p>
              </div>
              <CoverageIcon className="text-blue-700" data-testid={`coverage-icon-${isLiquor(c) ? "liquor_liability" : (c.type || "other")}`} />
            </div>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs font-bold uppercase text-slate-500">Insurance company</p>
                <p className="mt-1 font-medium">{c.insurer || "Not listed"}</p>
                <p className="text-sm text-slate-500">NAIC: {c.naic || "—"}</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase text-slate-500">Certificate contact</p>
                <p className="mt-1 font-medium">{c.certificateContact?.name || "Not listed"}</p>
                <p className="text-sm text-slate-500">{c.certificateContact?.phone || c.certificateContact?.email || "No certificate contact available"}</p>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button data-testid={`coverage-view-certificate-${i}`} disabled={viewing === c.document?.id} onClick={() => viewCertificate(c.document?.id)} variant="outline">
                <FileText size={16} />{viewing === c.document?.id ? "Opening…" : "View certificate"}
              </Button>
              {c.verification ? (
                <Link className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm" data-testid={`coverage-verification-status-${i}`} to={`/verification-request/${c.verification.id}`}>
                  <ShieldCheck size={16} />{c.verification.status.replaceAll("_", " ")}
                </Link>
              ) : (
                <Button data-testid={`coverage-request-verification-${i}`} disabled={requesting === c.document?.id} onClick={() => requestVerification(c.document?.id)}>
                  <ShieldCheck size={16} />{requesting === c.document?.id ? "Requesting…" : "Request verification"}
                </Button>
              )}
            </div>
          </article>
          );
        })}
        {!data.coverages.length && <p className="text-sm text-slate-500" data-testid="coverage-empty">No active certificate coverage is on file for this insured yet.</p>}
      </div>
    </section>
  );
}
